// Création d'un mandat — dialog M1.
// Contient : bloc vendeur (inline pour M1) + bloc véhicule minimal.
// Aucun sélecteur "vendeur existant" en M1 (interface minimaliste).

import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { mandatesApi } from "@/lib/api/mandates";

const Field = ({ label, children, required, testId }) => (
  <label className="flex flex-col gap-1.5" data-testid={`field-${testId}`}>
    <span className="text-[11.5px] uppercase tracking-[0.14em] text-black/55">
      {label}{required && <span className="text-[#EF4444] ml-1">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "h-10 rounded-lg bg-white px-3 text-[13.5px] text-black transition-shadow outline-none " +
  "focus:ring-2 focus:ring-[#C8FF2F]/40";
const inputStyle = { boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" };

export default function CreateMandateDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({
    seller_name: "", seller_email: "", seller_phone: "",
    brand: "", model: "", version: "",
    year: "", mileage: "", color: "", energy: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.seller_name.trim()) return toast.error("Le nom du vendeur est requis");
    if (!form.brand.trim())        return toast.error("La marque du véhicule est requise");
    if (!form.model.trim())        return toast.error("Le modèle du véhicule est requis");

    setSubmitting(true);
    try {
      const payload = {
        seller_inline: {
          name:  form.seller_name.trim(),
          email: form.seller_email.trim() || null,
          phone: form.seller_phone.trim() || null,
        },
        vehicle: {
          brand:   form.brand.trim(),
          model:   form.model.trim(),
          version: form.version.trim() || null,
          year:    form.year ? Number(form.year) : null,
          mileage: form.mileage ? Number(form.mileage) : null,
          color:   form.color.trim() || null,
          energy:  form.energy.trim() || null,
        },
        notes: form.notes.trim(),
      };
      const m = await mandatesApi.create(payload);
      onCreated?.(m);
      setForm({
        seller_name: "", seller_email: "", seller_phone: "",
        brand: "", model: "", version: "",
        year: "", mileage: "", color: "", energy: "",
        notes: "",
      });
    } catch (e2) {
      toast.error(e2.message || "Erreur lors de la création du mandat");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="create-mandate-dialog">
        <DialogHeader>
          <DialogTitle className="text-[20px] font-semibold tracking-[-0.01em]">
            Nouveau mandat
          </DialogTitle>
          <p className="text-[12.5px] text-black/50">
            Renseignez le vendeur et le véhicule. Vous pourrez enrichir la fiche
            plus tard dans le cockpit.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-6">
          {/* Vendeur */}
          <section className="space-y-3">
            <h3 className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-black/70">
              Vendeur
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Nom complet" required testId="seller-name">
                <input className={inputCls} style={inputStyle}
                       value={form.seller_name} onChange={set("seller_name")}
                       autoFocus data-testid="input-seller-name" />
              </Field>
              <Field label="Email" testId="seller-email">
                <input className={inputCls} style={inputStyle} type="email"
                       value={form.seller_email} onChange={set("seller_email")}
                       data-testid="input-seller-email" />
              </Field>
              <Field label="Téléphone" testId="seller-phone">
                <input className={inputCls} style={inputStyle}
                       value={form.seller_phone} onChange={set("seller_phone")}
                       placeholder="+33 6 ..." data-testid="input-seller-phone" />
              </Field>
            </div>
          </section>

          {/* Véhicule */}
          <section className="space-y-3">
            <h3 className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-black/70">
              Véhicule
            </h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Marque" required testId="brand">
                <input className={inputCls} style={inputStyle}
                       value={form.brand} onChange={set("brand")}
                       data-testid="input-brand" />
              </Field>
              <Field label="Modèle" required testId="model">
                <input className={inputCls} style={inputStyle}
                       value={form.model} onChange={set("model")}
                       data-testid="input-model" />
              </Field>
              <Field label="Version / finition" testId="version">
                <input className={inputCls} style={inputStyle}
                       value={form.version} onChange={set("version")}
                       data-testid="input-version" />
              </Field>
              <Field label="Année" testId="year">
                <input className={inputCls} style={inputStyle} type="number"
                       value={form.year} onChange={set("year")}
                       data-testid="input-year" />
              </Field>
              <Field label="Kilométrage" testId="mileage">
                <input className={inputCls} style={inputStyle} type="number"
                       value={form.mileage} onChange={set("mileage")}
                       data-testid="input-mileage" />
              </Field>
              <Field label="Couleur" testId="color">
                <input className={inputCls} style={inputStyle}
                       value={form.color} onChange={set("color")}
                       data-testid="input-color" />
              </Field>
              <Field label="Énergie" testId="energy">
                <input className={inputCls} style={inputStyle}
                       value={form.energy} onChange={set("energy")}
                       placeholder="Essence / Diesel / Hybride..."
                       data-testid="input-energy" />
              </Field>
            </div>
          </section>

          {/* Notes */}
          <Field label="Notes internes" testId="notes">
            <textarea
              rows={2}
              className="rounded-lg bg-white px-3 py-2 text-[13.5px] outline-none focus:ring-2 focus:ring-[#C8FF2F]/40"
              style={inputStyle}
              value={form.notes}
              onChange={set("notes")}
              data-testid="input-notes"
            />
          </Field>

          <DialogFooter className="mt-4 gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-full bg-white px-4 py-2 text-[13px] font-medium text-black/70 hover:text-black"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
              data-testid="dialog-cancel-btn"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-black px-5 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              data-testid="dialog-submit-btn"
            >
              {submitting ? "Création..." : "Créer le mandat"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
